/**
 * Process Preview Modal — Interactive.
 *
 * Fully functional preview of how a process appears to the person
 * filling it out. All fields are interactive — you can enter text,
 * check boxes, select options, tap Pass/Fail, etc. Nothing is saved
 * to the server; values live in local state only.
 *
 * A progress bar at the top tracks how many steps have been completed,
 * and a "Reset" button clears all values to start over.
 */

import { useState } from "react";
import {
  X,
  Camera,
  PenTool,
  Info,
  CheckCircle,
  Circle,
  Eye,
  FileText,
  RotateCcw,
} from "lucide-react";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";

interface PreviewStep {
  step_type: string;
  label: string;
  description?: string | null;
  is_required: boolean;
  section?: string | null;
  config?: Record<string, unknown> | null;
}

interface ProcessPreviewProps {
  name: string;
  description?: string | null;
  category?: string | null;
  icon?: string | null;
  estimatedMinutes?: number | null;
  scoringEnabled?: boolean;
  steps: PreviewStep[];
  onClose: () => void;
}

const NON_INPUT = new Set(["heading", "instruction"]);

function ProcessPreview({
  name,
  description,
  category,
  icon,
  estimatedMinutes,
  scoringEnabled,
  steps,
  onClose,
}: ProcessPreviewProps) {
  // Each step's value keyed by index.
  const [values, setValues] = useState<Record<number, unknown>>({});

  const setValue = (idx: number, val: unknown) =>
    setValues((prev) => ({ ...prev, [idx]: val }));

  const resetAll = () => setValues({});

  const inputSteps = steps.filter((s) => !NON_INPUT.has(s.step_type));
  const completedCount = inputSteps.filter((_, i) => {
    const realIdx = steps.indexOf(inputSteps[i]!);
    const v = values[realIdx];
    return v !== undefined && v !== "" && v !== null;
  }).length;
  const progress =
    inputSteps.length > 0 ? (completedCount / inputSteps.length) * 100 : 0;

  let currentSection: string | null = null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-[3px]"
        onClick={onClose}
      />

      <div className="relative z-10 w-full max-w-xl max-h-[85vh] overflow-hidden rounded-[var(--radius-xl)] elevation-3 flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-5 py-3.5 bg-[var(--surface-base)]">
          <div className="flex items-center gap-3 min-w-0">
            <Eye className="h-4 w-4 text-[var(--color-interactive-primary)]" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">
                {icon && <span className="mr-1">{icon}</span>}
                {name || "Untitled process"}
              </h3>
              <p className="text-[11px] text-[var(--text-tertiary)]">
                Interactive preview — try filling it out
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={resetAll}
              title="Reset all values"
              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Info + progress bar */}
        <div className="border-b border-[var(--border-subtle)] px-5 py-2.5 bg-[var(--surface-base)]">
          <div className="flex items-center gap-3 mb-2">
            {category && <Badge variant="info">{category}</Badge>}
            <span className="text-xs text-[var(--text-tertiary)]">
              {inputSteps.length} steps
            </span>
            {estimatedMinutes != null && (
              <span className="text-xs text-[var(--text-tertiary)]">
                ~{estimatedMinutes} min
              </span>
            )}
            {scoringEnabled && <Badge variant="warning">Scored</Badge>}
            {description && (
              <span className="text-xs text-[var(--text-secondary)] truncate">
                {description}
              </span>
            )}
            <span className="ml-auto text-xs font-medium text-[var(--color-interactive-primary)]">
              {completedCount}/{inputSteps.length} completed
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[var(--border-subtle)] overflow-hidden">
            <div
              className="h-full rounded-full bg-[var(--color-interactive-primary)] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1 bg-[var(--surface-sunken)]">
          {steps.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-10 w-10 text-[var(--text-tertiary)]" />
              <p className="mt-2 text-sm text-[var(--text-secondary)]">
                No steps added yet
              </p>
            </div>
          ) : (
            steps.map((step, i) => {
              const showSection =
                step.section && step.section !== currentSection;
              if (step.section) currentSection = step.section;
              return (
                <div key={i}>
                  {showSection && (
                    <p className="text-[9px] font-medium uppercase tracking-[0.1em] text-[var(--text-tertiary)] pt-4 pb-1 px-1">
                      {step.section}
                    </p>
                  )}
                  <InteractiveStep
                    step={step}
                    value={values[i]}
                    onChange={(v) => setValue(i, v)}
                  />
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-[var(--border-subtle)] px-5 py-3 bg-[var(--surface-base)]">
          <p className="text-[11px] text-[var(--text-tertiary)]">
            Preview only — nothing is saved
          </p>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function InteractiveStep({
  step,
  value,
  onChange,
}: {
  step: PreviewStep;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const config = step.config || {};
  const isCompleted = value !== undefined && value !== "" && value !== null;

  // --- Heading ---
  if (step.step_type === "heading") {
    return (
      <div className="pt-3 pb-1">
        <h4 className="text-sm font-semibold text-[var(--text-primary)]">
          {step.label}
        </h4>
        {step.description && (
          <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
            {step.description}
          </p>
        )}
      </div>
    );
  }

  // --- Instruction ---
  if (step.step_type === "instruction") {
    return (
      <div className="rounded-[var(--radius-md)] bg-[var(--fill-info)] border border-[var(--text-info)]/20 px-3 py-2.5 my-1">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-[var(--text-info)] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-[var(--text-primary)]">{step.label}</p>
            {step.description && (
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {step.description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-[var(--radius-md)] border px-3 py-2.5 my-0.5 transition-colors ${
        isCompleted
          ? "bg-[var(--surface-base)] border-[var(--color-interactive-primary)]/20"
          : "bg-[var(--surface-base)] border-[var(--border-subtle)]"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5">
          {isCompleted ? (
            <CheckCircle className="h-4 w-4 text-[var(--text-success)]" />
          ) : (
            <Circle className="h-4 w-4 text-[var(--text-tertiary)]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-medium ${isCompleted ? "text-[var(--text-secondary)]" : "text-[var(--text-primary)]"}`}
            >
              {step.label}
            </span>
            {step.is_required && (
              <span className="text-[10px] font-medium text-[var(--text-danger)]">
                Required
              </span>
            )}
          </div>
          {step.description && (
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              {step.description}
            </p>
          )}

          <div className="mt-2">
            {/* Checkbox */}
            {step.step_type === "checkbox" && (
              <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(e) => onChange(e.target.checked || undefined)}
                  className="h-4 w-4 rounded border-[var(--border-default)] text-[var(--color-interactive-primary)] focus:ring-[var(--border-focus)]"
                />
                Done
              </label>
            )}

            {/* Text */}
            {step.step_type === "text" && (
              <input
                type="text"
                value={String(value || "")}
                onChange={(e) => onChange(e.target.value || undefined)}
                placeholder="Enter text..."
                className="w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
            )}

            {/* Number / Meter reading */}
            {(step.step_type === "number" ||
              step.step_type === "meter_reading") && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={String(value ?? "")}
                  onChange={(e) =>
                    onChange(
                      e.target.value ? parseFloat(e.target.value) : undefined,
                    )
                  }
                  min={config.min != null ? Number(config.min) : undefined}
                  max={config.max != null ? Number(config.max) : undefined}
                  step="any"
                  placeholder={config.min != null ? String(config.min) : "0"}
                  className="flex-1 h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                {config.unit != null && (
                  <span className="text-xs font-medium text-[var(--text-secondary)]">
                    {String(config.unit)}
                  </span>
                )}
              </div>
            )}

            {/* Amount */}
            {step.step_type === "amount" && (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--text-tertiary)]">
                  $
                </span>
                <input
                  type="number"
                  value={String(value ?? "")}
                  onChange={(e) =>
                    onChange(
                      e.target.value ? parseFloat(e.target.value) : undefined,
                    )
                  }
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] pl-7 pr-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            )}

            {/* Date */}
            {step.step_type === "date" && (
              <input
                type="date"
                value={String(value || "")}
                onChange={(e) => onChange(e.target.value || undefined)}
                className="w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
            )}

            {/* Select */}
            {step.step_type === "select" && (
              <select
                value={String(value || "")}
                onChange={(e) => onChange(e.target.value || undefined)}
                className="w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              >
                <option value="">Select an option...</option>
                {((config.options || []) as string[]).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}

            {/* Multiselect / Checklist */}
            {(step.step_type === "multiselect" ||
              step.step_type === "checklist") &&
              (() => {
                const selected = (value as Record<string, boolean>) || {};
                const items = (config.options ||
                  config.items ||
                  []) as string[];
                return (
                  <div className="space-y-1.5">
                    {items.map((opt) => (
                      <label
                        key={opt}
                        className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={!!selected[opt]}
                          onChange={(e) => {
                            const next = {
                              ...selected,
                              [opt]: e.target.checked,
                            };
                            const anyChecked =
                              Object.values(next).some(Boolean);
                            onChange(anyChecked ? next : undefined);
                          }}
                          className="h-4 w-4 rounded border-[var(--border-default)] text-[var(--color-interactive-primary)] focus:ring-[var(--border-focus)]"
                        />
                        {opt}
                      </label>
                    ))}
                    {items.length === 0 && (
                      <p className="text-xs text-[var(--text-tertiary)] italic">
                        No options configured
                      </p>
                    )}
                  </div>
                );
              })()}

            {/* Inspection — Pass / Flag / Fail */}
            {step.step_type === "inspection" && (
              <div className="flex gap-1.5">
                {(["pass", "flag", "fail"] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => onChange(value === v ? undefined : v)}
                    className={`rounded-[var(--radius-md)] border px-3 py-1.5 text-xs font-medium transition-all ${
                      value === v
                        ? v === "pass"
                          ? "border-[var(--text-success)] bg-[var(--text-success)] text-white"
                          : v === "flag"
                            ? "border-[var(--text-warning)] bg-[var(--text-warning)] text-white"
                            : "border-[var(--text-danger)] bg-[var(--text-danger)] text-white"
                        : v === "pass"
                          ? "border-[var(--text-success)]/30 text-[var(--text-success)] hover:bg-[var(--fill-success)]"
                          : v === "flag"
                            ? "border-[var(--text-warning)]/30 text-[var(--text-warning)] hover:bg-[var(--fill-warning)]"
                            : "border-[var(--text-danger)]/30 text-[var(--text-danger)] hover:bg-[var(--fill-danger)]"
                    }`}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            )}

            {/* Yes / No / N/A */}
            {step.step_type === "yes_no_na" && (
              <div className="flex gap-1.5">
                {(["yes", "no", "na"] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => onChange(value === v ? undefined : v)}
                    className={`rounded-[var(--radius-md)] border px-3 py-1.5 text-xs font-medium transition-all ${
                      value === v
                        ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)] text-white"
                        : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--color-interactive-primary)]/30 hover:text-[var(--color-interactive-primary)]"
                    }`}
                  >
                    {v === "na"
                      ? "N/A"
                      : v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            )}

            {/* Photo */}
            {step.step_type === "photo" && (
              <button
                onClick={() => onChange(value ? undefined : "photo_selected")}
                className={`w-full h-20 rounded-[var(--radius-md)] border-2 border-dashed flex items-center justify-center gap-2 text-sm transition-colors ${
                  value
                    ? "border-[var(--text-success)]/30 bg-[var(--fill-success)] text-[var(--text-success)]"
                    : "border-[var(--border-default)] text-[var(--text-tertiary)] hover:border-[var(--color-interactive-primary)]/30 hover:text-[var(--color-interactive-primary)]"
                }`}
              >
                <Camera className="h-4 w-4" />
                {value ? "Photo attached ✓" : "Tap to upload photo"}
              </button>
            )}

            {/* Signature */}
            {step.step_type === "signature" && (
              <button
                onClick={() =>
                  onChange(value ? undefined : "signature_captured")
                }
                className={`w-full h-16 rounded-[var(--radius-md)] border-2 border-dashed flex items-center justify-center gap-2 text-sm transition-colors ${
                  value
                    ? "border-[var(--text-success)]/30 bg-[var(--fill-success)] text-[var(--text-success)]"
                    : "border-[var(--border-default)] text-[var(--text-tertiary)] hover:border-[var(--color-interactive-primary)]/30 hover:text-[var(--color-interactive-primary)]"
                }`}
              >
                <PenTool className="h-4 w-4" />
                {value ? "Signed ✓" : "Tap to sign"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessPreview;
